import equal from "fast-deep-equal";
import React from "react";
import { Col, Container, Row, Tab, Tabs } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import DescriptionTab from "../../components/descriptionTab/DescriptionTab";
import APIErrorHelper from "../../helpers/APIErrorHelper";
import CamplaCoreService from "../../services/CamplaCoreService";
import StorageService from "../../services/StorageService";
import EditTemplateDialog from "./dialogs/editTemplateDialog/EditTemplateDialog";
import InstanceTemplateInfoCard from "./instanceTemplateInfoCard/InstanceTemplateInfoCard";
import "./SingleInstanceTemplate.css";
import ConfigurationTab from "./tabs/configurationTab/ConfigurationTab";
import ExamsTab from "./tabs/examsTab/ExamsTab";
import ScriptsTab from "./tabs/scriptsTab/ScriptsTab";

class SingleInstanceTemplate extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      instanceTemplate: null,
      preferredNetworkProfile: null,
      networkProfiles: null,
      softwareCatalogs: null,
      associatedSoftwareCatalogs: null,
      templateSoftware: [],
      configurationTemplates: [],
      apiError: "",
      dialogApiError: "",
      showEditDialog: false,
      isSaving: false,
      validationResult: "",
    };

    this.fetchData = this.fetchData.bind(this);
    this.onSubmitInstanceTemplate = this.onSubmitInstanceTemplate.bind(this);
    this.onSubmitConfigurationConfig = this.onSubmitConfigurationConfig.bind(
      this
    );
    this.onEdit = this.onEdit.bind(this);
  }

  async componentDidMount() {
    const cachedInstanceTemplate = StorageService.getReferenceType(
      "singleInstanceTemplate"
    );
    const cachedNetworkProfiles = StorageService.getReferenceType(
      "networkProfiles"
    );
    const cachedSoftwareCatalogs = StorageService.getReferenceType(
      "softwareCatalogs"
    );
    const cachedPreferredNetworkProfile = StorageService.getReferenceType(
      "preferredNetworkProfile"
    );
    const associatedSoftwareCatalogs = StorageService.getReferenceType(
      "associatedSoftwareCatalogs"
    );
    const cachedConfigurationTemplates = StorageService.getReferenceType(
      "configurationTemplates"
    );
    const templateSoftware = StorageService.getReferenceType(
      "templateSoftware"
    );
    if (
      cachedInstanceTemplate &&
      cachedInstanceTemplate._id === this.props.match.params.id
    ) {
      this.setState({ instanceTemplate: cachedInstanceTemplate });
    }

    if (cachedNetworkProfiles)
      this.setState({ networkProfiles: cachedNetworkProfiles });

    if (cachedConfigurationTemplates)
      this.setState({ configurationTemplates: cachedConfigurationTemplates });

    if (cachedSoftwareCatalogs)
      this.setState({ softwareCatalogs: cachedSoftwareCatalogs });

    if (associatedSoftwareCatalogs)
      this.setState({ associatedSoftwareCatalogs });

    if (templateSoftware) this.setState({ templateSoftware });

    if (cachedPreferredNetworkProfile)
      this.setState({ preferredNetworkProfile: cachedPreferredNetworkProfile });

    await this.fetchData();
  }

  onEdit() {
    this.setState({ showEditDialog: true });
  }

  async fetchData() {
    try {
      const instanceTemplate = await CamplaCoreService.getInstanceTemplate(
        this.props.match.params.id
      );
      const networkProfiles = await CamplaCoreService.getAllNetworkProfiles();
      const softwareCatalogs = await CamplaCoreService.getAllSoftwareCatalogs();
      const templateSoftware = await CamplaCoreService.getAllTemplateSoftware();
      const configurationTemplates = await CamplaCoreService.getInstanceTemplateConfigurations();
      const associatedSoftwareCatalogs = softwareCatalogs?.filter((sc) =>
        templateSoftware.find(
          (ts) =>
            ts.instanceTemplateId === instanceTemplate._id &&
            ts.softwareCatalogId === sc._id
        )
      );
      StorageService.set(
        "associatedSoftwareCatalogs",
        associatedSoftwareCatalogs
      );
      StorageService.set("networkProfiles", networkProfiles);
      StorageService.set("singleInstanceTemplate", instanceTemplate);
      StorageService.set("softwareCatalogs", softwareCatalogs);
      StorageService.set("templateSoftware", templateSoftware);
      StorageService.set("configurationTemplates", configurationTemplates);

      if (instanceTemplate.preferredNetworkProfile) {
        const preferredNetworkProfile = await CamplaCoreService.getNetworkProfile(
          instanceTemplate.preferredNetworkProfile
        );
        StorageService.set("preferredNetworkProfile", preferredNetworkProfile);
        this.setState({ preferredNetworkProfile });
      }

      this.setState({
        instanceTemplate,
        networkProfiles,
        softwareCatalogs,
        associatedSoftwareCatalogs,
        templateSoftware,
        configurationTemplates,
      });
    } catch (err) {
      await this.setState({
        apiError: APIErrorHelper.getError(
          err,
          () => this.setState({ apiError: "" }),
          {},
          this.props.t
        ),
      });
    }
  }

  async assignSoftwareCatalogsToTemplate(softwareCatalogs) {
    if (!equal(softwareCatalogs, this.state.associatedSoftwareCatalogs)) {
      for (const catalog of softwareCatalogs) {
        if (
          !this.state.associatedSoftwareCatalogs.find(
            (s) => s._id === catalog._id
          )
        ) {
          const payload = {
            instanceTemplateId: this.state.instanceTemplate._id,
            softwareCatalogId: catalog._id,
          };
          const createdTemplateSoftware = await CamplaCoreService.createTemplateSoftware(
            payload
          );
          this.setState({
            templateSoftware: [
              ...this.state.templateSoftware,
              createdTemplateSoftware,
            ],
          });
        }
      }
      for (const catalog of this.state.associatedSoftwareCatalogs) {
        if (!softwareCatalogs.find((s) => s._id === catalog._id)) {
          const templateSoftware = this.state.templateSoftware.find(
            (ts) =>
              ts.instanceTemplateId === this.state.instanceTemplate._id &&
              ts.softwareCatalogId === catalog._id
          );
          if (templateSoftware)
            await CamplaCoreService.deleteTemplateSoftware(
              templateSoftware._id
            );
        }
      }
    }
  }

  async onSubmitConfigurationConfig(config) {
    this.setState({ isSaving: true });
    const template = Object.assign(this.state.instanceTemplate, {
      configuration: btoa(config),
    });
    template["userId"] = CamplaCoreService.getAuthenticatedUser()._id;
    await this.onSubmitInstanceTemplate(template);
    this.setState({ isSaving: false });
  }

  async onSubmitInstanceTemplate(changedInstanceTemplate) {
    this.setState({ isSaving: true });
    try {
      if (changedInstanceTemplate.softwareCatalogs) {
        await this.updateTemplateSoftware(changedInstanceTemplate);
      }
      const updatedInstanceTemplate = await CamplaCoreService.updateInstanceTemplate(
        this.state.instanceTemplate._id,
        changedInstanceTemplate
      );
      StorageService.set("singleInstanceTemplate", updatedInstanceTemplate);
      this.setState({
        instanceTemplate: updatedInstanceTemplate,
        showEditDialog: false,
        dialogApiError: "",
        validationResult: "",
      });
    } catch (err) {
      try {
        const result = await CamplaCoreService.validateInstanceTemplate(
          changedInstanceTemplate
        );
        if (result.valid) {
          this.showApiError(err);
        } else {
          this.setState({
            validationResult: result.report.init_error,
          });
        }
      } catch (err) {
        this.showApiError(err);
      }
    }
    this.setState({ isSaving: false });
  }

  showApiError(err) {
    this.setState({
      dialogApiError: APIErrorHelper.getError(
        err,
        () => this.setState({ dialogApiError: "", validationResult: "" }),
        {
          PRECONDITION_FAILED:
            "SingleInstanceTemplate.APIError.PreconditionFailed",
          NOT_FOUND: "SingleInstanceTemplate.APIError.NotFound",
          CONFLICT: "SingleInstanceTemplate.APIError.UpdateConflict",
        },
        this.props.t
      ),
    });
  }

  async updateTemplateSoftware(changedInstanceTemplate) {
    await this.assignSoftwareCatalogsToTemplate(
      changedInstanceTemplate.softwareCatalogs
    );
    const associatedSoftwareCatalogs = changedInstanceTemplate.softwareCatalogs;
    delete changedInstanceTemplate.softwareCatalogs;
    StorageService.set(
      "associatedSoftwareCatalogs",
      associatedSoftwareCatalogs
    );
    this.setState({ associatedSoftwareCatalogs });
  }

  render() {
    const { t } = this.props;
    return !this.state.instanceTemplate ? (
      <div />
    ) : (
      <div>
        <Container className="mt-3">
          {this.state.apiError ? (
            <Row>
              <Col>{this.state.apiError}</Col>
            </Row>
          ) : (
            ""
          )}
          <EditTemplateDialog
            show={this.state.showEditDialog}
            networkProfiles={this.state.networkProfiles}
            softwareCatalogs={this.state.softwareCatalogs}
            preferredNetworkProfile={this.state.preferredNetworkProfile}
            instanceTemplate={this.state.instanceTemplate}
            associatedSoftwareCatalogs={this.state.associatedSoftwareCatalogs}
            dialogApiError={this.state.dialogApiError}
            onHide={() => this.setState({ showEditDialog: false })}
            onSubmit={this.onSubmitInstanceTemplate}
            isSaving={this.state.isSaving}
          />
          <Row>
            <Col>
              <InstanceTemplateInfoCard
                instanceTemplate={this.state.instanceTemplate}
                preferredNetworkProfile={
                  this.state.preferredNetworkProfile
                    ? this.state.preferredNetworkProfile.name
                    : ""
                }
                softwareCatalogs={this.state.associatedSoftwareCatalogs}
                onEdit={this.onEdit}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              {this.state.instanceTemplate &&
                this.state.associatedSoftwareCatalogs &&
                this.state.networkProfiles &&
                this.state.softwareCatalogs && (
                  <Tabs defaultActiveKey="description">
                    <Tab
                      eventKey="description"
                      tabClassName="item-link"
                      title={t("SingleInstanceTemplate.DescriptionTabTitle")}
                    >
                      <DescriptionTab
                        text={this.state.instanceTemplate.description}
                        lang={this.props.i18n.language}
                      />
                    </Tab>
                    <Tab
                      eventKey="instances"
                      tabClassName="item-link"
                      title={t("SingleInstanceTemplate.ConfigurationTabTitle")}
                    >
                      <ConfigurationTab
                        instanceTemplate={this.state.instanceTemplate}
                        networkProfiles={this.state.networkProfiles}
                        softwareCatalogs={this.state.softwareCatalogs}
                        configurationTemplates={
                          this.state.configurationTemplates
                        }
                        associatedSoftwareCatalogs={
                          this.state.associatedSoftwareCatalogs
                        }
                        onSubmit={this.onSubmitConfigurationConfig}
                        isSaving={this.state.isSaving}
                        validationResult={this.state.validationResult}
                      />
                    </Tab>
                    <Tab
                      eventKey="examinationFiles"
                      tabClassName="item-link"
                      title={t("SingleInstanceTemplate.ScriptsTabTitle")}
                    >
                      <ScriptsTab
                        instanceTemplate={this.state.instanceTemplate}
                      />
                    </Tab>
                    <Tab
                      eventKey="exams"
                      tabClassName="item-link"
                      title={t("SingleInstanceTemplate.ExamsTabTitle")}
                    >
                      <ExamsTab
                        instanceTemplate={this.state.instanceTemplate}
                      />
                    </Tab>
                  </Tabs>
                )}
            </Col>
          </Row>
        </Container>
      </div>
    );
  }
}

export default withTranslation()(SingleInstanceTemplate);
